<script lang="ts">
  import { tick } from 'svelte';
  import { audio } from './audio/AudioManager';
  import { animateViewEntrance, createMotionScope, type MotionScope } from './game/motion';
  import { router } from './ui/stores/router.svelte';
  import { session } from './ui/stores/session.svelte';
  import { settings } from './ui/stores/settings.svelte';

  import MenuView from './ui/views/MenuView.svelte';
  import PlayView from './ui/views/PlayView.svelte';
  import SoloView from './ui/views/SoloView.svelte';
  import VersusView from './ui/views/VersusView.svelte';
  import RankingsView from './ui/views/RankingsView.svelte';
  import HistoryView from './ui/views/HistoryView.svelte';
  import ProfileView from './ui/views/ProfileView.svelte';
  import AchievementsView from './ui/views/AchievementsView.svelte';
  import HighScoresView from './ui/views/HighScoresView.svelte';
  import SettingsView from './ui/views/SettingsView.svelte';
  import LoginView from './ui/views/LoginView.svelte';
  import RegisterView from './ui/views/RegisterView.svelte';
  import PixelSprite from './ui/components/PixelSprite.svelte';
  import ToastHost from './ui/components/ToastHost.svelte';

  const VIEWS = {
    menu: MenuView,
    play: PlayView,
    solo: SoloView,
    versus: VersusView,
    rankings: RankingsView,
    history: HistoryView,
    profile: ProfileView,
    achievements: AchievementsView,
    highscores: HighScoresView,
    settings: SettingsView,
    login: LoginView,
    register: RegisterView,
  } as const;

  /**
   * The identity gate.
   *
   * A device with no chosen identity gets the sign-in screen whatever the hash
   * says, and REGISTRARSE is the one other door out of it — it is the second
   * way to acquire an identity, not a way around needing one. Everything else
   * would be a screen about a player who does not exist yet.
   *
   * Done here rather than as a router guard on purpose: a guard would have to
   * fight every entry point the hash offers (a typed URL, Android's back key, a
   * deep link from a notification) and would still lose the race with the first
   * paint. Choosing the component is one decision, in one place, that no
   * navigation can slip past.
   */
  const gated = $derived(session.ready && !session.hasIdentity);
  const Current = $derived(
    gated
      ? router.current.name === 'register'
        ? RegisterView
        : LoginView
      : VIEWS[router.current.name],
  );
  const viewKey = $derived(
    gated
      ? `gate:${router.current.name === 'register' ? 'register' : 'login'}`
      : `${router.current.name}:${JSON.stringify(router.current.params)}`,
  );
  let viewRoot: HTMLDivElement;
  let transitionRun = 0;
  /**
   * One GSAP context per mounted view, reverted before the next one is built.
   * Killing the timeline alone would leave the inline styles it wrote on a
   * subtree Svelte is about to discard; `revert()` also undoes those, so a view
   * that is navigated away from mid-entrance cannot leave a half-faded node
   * behind if it is ever reused.
   */
  let viewScope: MotionScope | undefined;

  $effect(() => {
    const stop = router.start();
    void session.init();
    return stop;
  });

  $effect(() => {
    const routeAtStart = viewKey;
    audio.playMusic(
      router.current.name === 'solo' || router.current.name === 'versus' ? 'match' : 'lobby',
    );
    const run = ++transitionRun;
    viewScope?.destroy();
    viewScope = undefined;

    void tick().then(() => {
      if (run !== transitionRun || routeAtStart !== viewKey || !viewRoot) return;
      const view = viewRoot.querySelector<HTMLElement>(':scope > .view');
      if (!view) return;

      const scope = createMotionScope(view);
      scope.add(() => animateViewEntrance(view));
      viewScope = scope;
    });

    return () => {
      viewScope?.destroy();
      viewScope = undefined;
    };
  });
</script>

<div class="app-frame">
  <PixelSprite />
  <div class="app-frame__inner" data-route={router.current.name}>
    <!--
      Views mount here, and this is the element that animates. Note what is NOT
      inside it: the CRT layers below. Keeping them out means a view transition
      composites the view alone, instead of dragging a full-screen repeating
      gradient and an inset shadow along with it every frame.

      `{#key}` forces a real unmount when the route changes, which is what
      triggers SoloView's cleanup and kills its GSAP context.
    -->
    <!--
      Nothing is painted until the identity is resolved. It is a storage read,
      so this is a frame or two — but rendering the menu first and correcting it
      to the sign-in screen afterwards would show every returning player a login
      form flashing past on every cold start.
    -->
    <div class="app-view" bind:this={viewRoot}>
      {#if session.ready}
        {#key viewKey}
          <Current />
        {/key}
      {/if}
    </div>

    <!--
      Outside `.app-view` on purpose: a toast announcing a lost connection or an
      expired code usually arrives *during* a navigation, and inside that
      element it would slide away with the screen it was talking about.
    -->
    <ToastHost />

    <!-- The CRT layers are a preference, not furniture: someone who finds
         scanlines unreadable can turn them off, and then they must not be in
         the tree at all rather than merely transparent. -->
    {#if settings.display.crt}
      <div class="screen-fx" aria-hidden="true">
        <div class="screen-fx__scanlines"></div>
        <div class="screen-fx__vignette"></div>
      </div>
    {/if}
  </div>
</div>
